import { useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

const VIDEO_URL =
    "https://customer-assets.emergentagent.com/job_narcan-dispenser-pro/artifacts/v9rtn25s_higgsfield-31d7fc59-9365-4a64-91ed-05c0cb7d7b3c.mp4";

export const VideoShowcase = () => {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(true);
    const [muted, setMuted] = useState(true);

    const toggle = () => {
        const v = ref.current;
        if (!v) return;
        if (v.paused) {
            v.play();
            setPlaying(true);
        } else {
            v.pause();
            setPlaying(false);
        }
    };

    const toggleMute = () => {
        const v = ref.current;
        if (!v) return;
        v.muted = !v.muted;
        setMuted(v.muted);
    };

    return (
        <section
            id="video"
            data-testid="video-section"
            className="relative bg-[#0A1428] text-white py-16 md:py-24 overflow-hidden"
        >
            <div className="absolute inset-0 dot-grid-light opacity-20 pointer-events-none" />

            <div className="relative px-4 sm:px-6 md:px-10">
                <div className="reveal flex items-center gap-3 max-w-7xl mx-auto mb-8">
                    <span className="relative inline-flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#F0493E] pulse-ring" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F0493E]" />
                    </span>
                    <span className="text-xs md:text-sm font-bold uppercase tracking-[0.32em] text-[#F0493E]">
                        The Kairos Unit
                    </span>
                </div>

                <div
                    data-testid="video-frame"
                    className="reveal relative overflow-hidden group"
                >
                    <div className="relative aspect-video w-full">
                        <video
                            ref={ref}
                            data-testid="video-player"
                            src={VIDEO_URL}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="auto"
                            className="absolute inset-0 w-full h-full object-cover"
                        />

                        {/* Vignette to blend video edges into the dark section */}
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(ellipse at center, transparent 55%, rgba(10,20,40,0.55) 92%, #0A1428 100%)",
                            }}
                        />
                        {/* Soft edge fades */}
                        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#0A1428]/55 to-transparent pointer-events-none" />
                        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0A1428] via-[#0A1428]/40 to-transparent pointer-events-none" />
                        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0A1428]/60 to-transparent pointer-events-none" />
                        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0A1428]/60 to-transparent pointer-events-none" />

                        {/* Controls — hidden until hover for an editorial feel */}
                        <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <button
                                data-testid="video-mute-toggle"
                                onClick={toggleMute}
                                aria-label={muted ? "Unmute" : "Mute"}
                                className="h-10 w-10 grid place-items-center bg-black/40 backdrop-blur-md border border-white/15 hover:bg-[#F0493E] transition-colors rounded-full"
                            >
                                {muted ? (
                                    <VolumeX
                                        size={16}
                                        strokeWidth={1.5}
                                        className="text-white"
                                    />
                                ) : (
                                    <Volume2
                                        size={16}
                                        strokeWidth={1.5}
                                        className="text-white"
                                    />
                                )}
                            </button>
                            <button
                                data-testid="video-play-toggle"
                                onClick={toggle}
                                aria-label={playing ? "Pause" : "Play"}
                                className="h-10 w-10 grid place-items-center bg-black/40 backdrop-blur-md border border-white/15 hover:bg-[#F0493E] transition-colors rounded-full"
                            >
                                {playing ? (
                                    <Pause
                                        size={16}
                                        strokeWidth={2}
                                        className="text-white"
                                    />
                                ) : (
                                    <Play
                                        size={16}
                                        strokeWidth={2}
                                        className="text-white"
                                    />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
